import {
  Box,
  BoxProps,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import DayPicker from 'components/common/DayPicker'
import { TEST_SCHEDULE_WINDOW_DURATIONS } from 'components/services/service-utils'
import { ScheduleDay } from 'types/schedules/default'
import { WEEK_DAYS } from 'utils/days'

// Adjusts time to account for user-initiated time zone change
function adjustTimeToZone(timeZone: string, value: string): Moment {
  return timeZone === 'UTC' ? moment.utc(value) : moment(value)
}

export const ScheduleWindow: React.FC<
  {
    disabled?: boolean
    activeDayFieldName: string
    startTimeFieldName: string
    endTimeFieldName: string
  } & BoxProps
> = ({
  disabled = false,
  activeDayFieldName,
  startTimeFieldName,
  endTimeFieldName,
  ...rest // used for the styling properties of the Box wrapper
}) => {
  const { control, getValues, setValue, watch } = useFormContext()

  const start: string = watch(startTimeFieldName)
  const end: string = getValues(endTimeFieldName)
  const activeDays: ScheduleDay[] = getValues(activeDayFieldName) || [
    ScheduleDay.Monday
  ]

  // Get current user timezone for display
  const userTimeZone = moment.tz.guess()
  const [timeZone, setTimeZone] = useState('UTC')
  const [duration, setDuration] = useState(
    moment(end).diff(moment(start), 'hours')
  )

  // Sets the default moment timezone. This is necessary to establish a root
  // timezone for the inner workings of the MUI TimePicker component
  moment.tz.setDefault(timeZone)

  /**
   * moment persists the set timezone even upon teardown of the component. This was causing
   * the time formatting on some component to be UTC based if this component was ever rendered
   * in the same view session.
   */
  useEffect(() => {
    return () => {
      moment.tz.setDefault(userTimeZone)
    }
  }, [userTimeZone])

  /**
   * Watches the start time and adjusts the active day accordingly. This is essential for the
   * operation of the user-selected time zone view.
   */
  useEffect(() => {
    setValue(activeDayFieldName, [
      WEEK_DAYS[adjustTimeToZone(timeZone, start).day()]
    ])
  }, [start, timeZone, activeDayFieldName, setValue])

  const handleChangeTimeZone = (e) => {
    const newTimeZone = e.target.value
    const newStart = adjustTimeToZone(newTimeZone, start)
    const newEnd = adjustTimeToZone(newTimeZone, end)

    // Anchors day picker display to reference and respond to the start time
    setValue(startTimeFieldName, newStart.format())
    setValue(endTimeFieldName, newEnd.format())
    setTimeZone(newTimeZone)
  }

  const handleChangeActiveDay = (day, onChange) => {
    const dayIndex = WEEK_DAYS.indexOf(day)
    let endDayIndex = dayIndex

    const startMoment = adjustTimeToZone(timeZone, start)
    const endMoment = adjustTimeToZone(timeZone, end)

    // Ensure that the schedule window follows the start day exactly
    if (endMoment.day() !== startMoment.day()) {
      // End day will always be after the start day
      const diff = endMoment.day() - startMoment.day()
      endDayIndex += diff
    }

    // Start and End days must shift in sync with the active day
    setValue(startTimeFieldName, startMoment.day(dayIndex).format())
    setValue(endTimeFieldName, endMoment.day(endDayIndex).format())
    onChange([day])
  }

  const handleChangeStartTime = (value, onChange) => {
    // Shift end time to maintain user-defined test duration
    const newEnd = adjustTimeToZone(timeZone, value).add(duration, 'hours')

    setValue(endTimeFieldName, newEnd.format())
    onChange(value.format())
  }

  const handleChangeDuration = (event) => {
    const newDuration = event.target.value
    const newEnd = moment(start).add(newDuration, 'hours')

    setDuration(newDuration)

    // Set the "shouldDirty" option to true in order to render the form dirty when setting this value
    // This flag needs to be set because "end" is not a value that is directly entered into the form.  It
    // is a value derived from other form values and local state values.  In order to make this change affect
    // the dirty state of the form, we set this flag manually.
    setValue(endTimeFieldName, newEnd.format(), { shouldDirty: true })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box data-test-schedule-window {...rest}>
        <Stack sx={{ gap: 5 }}>
          <Box>
            <Controller
              control={control}
              name={activeDayFieldName}
              render={({ field }) => (
                <DayPicker
                  activeDays={field.value}
                  disabled={disabled}
                  isStatic={disabled}
                  onChange={(day) => handleChangeActiveDay(day, field.onChange)}
                />
              )}
            />
          </Box>
          <Stack direction="row" gap={3} sx={{ width: '60%' }}>
            {/* Date/Time values for the MUI TimePicker are stored internally as Momentjs objects */}
            <Controller
              control={control}
              name={startTimeFieldName}
              render={({ field }) => (
                <TimePicker
                  disabled={disabled}
                  label="Window Start Hour"
                  openTo="hours"
                  PaperProps={{
                    sx: {
                      '.MuiClockPicker-arrowSwitcher': {
                        display: 'none' // Hides the arrows for switching between hours, minutes, and seconds views
                      }
                    }
                  }}
                  renderInput={(params) => {
                    // For some reason, it is necessary to set the value like this in the rendered input as well as
                    // the top level component in order to accommodate timezone shift
                    params.inputProps = {
                      'data-test-schedule-window-start-time-picker-input': true,
                      value: adjustTimeToZone(timeZone, field.value).format(
                        'hh:mm A'
                      )
                    }

                    return (
                      <TextField
                        fullWidth
                        helperText={`Starts ${moment(start).format(
                          `h:mm A${activeDays.length > 0 ? ', dddd' : ''}`
                        )}`}
                        {...params}
                      />
                    )
                  }}
                  value={adjustTimeToZone(timeZone, field.value)}
                  views={['hours']} // Only display the hours selector view
                  onChange={(value) =>
                    handleChangeStartTime(value, field.onChange)
                  }
                />
              )}
            />

            <FormControl sx={{ minWidth: 'fit-content', width: 'min-content' }}>
              <InputLabel id="window-length">Window Length</InputLabel>
              <Select
                data-test-schedule-window-length-select
                fullWidth
                disabled={disabled}
                inputProps={{
                  'data-test-schedule-window-length-select-input': true
                }}
                label="Window Length"
                labelId="window-length"
                value={duration}
                onChange={handleChangeDuration}
              >
                {TEST_SCHEDULE_WINDOW_DURATIONS.map((value, index) => (
                  <MenuItem
                    key={`${index}-${value}`}
                    data-test-schedule-window-length={`${value} hours`}
                    value={value}
                  >
                    {`${value} hours`}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Ends{' '}
                {moment(end).format(
                  `h:mm A${activeDays.length > 0 ? ', dddd' : ''}`
                )}
              </FormHelperText>
            </FormControl>

            <FormControl sx={{ minWidth: 'fit-content', width: 'min-content' }}>
              <InputLabel id="time-zone">Time Zone</InputLabel>
              <Select
                data-test-schedule-window-timezone-select
                fullWidth
                disabled={disabled}
                inputProps={{
                  'data-test-schedule-window-timezone-select-input': true
                }}
                label="Time Zone"
                labelId="time-zone"
                value={timeZone}
                onChange={handleChangeTimeZone}
              >
                <MenuItem
                  data-test-schedule-window-timezone="Local"
                  value={userTimeZone}
                >{`${userTimeZone} (${moment.tz
                  .zone(userTimeZone)
                  .abbr(moment.now())})`}</MenuItem>
                <MenuItem data-test-schedule-window-timezone="UTC" value="UTC">
                  UTC
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>
    </LocalizationProvider>
  )
}
